import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { animeApi } from "../../api/anime";
import { AnimeCard } from "../../components/AnimeCard";
import { ErrorState, PosterGridSkeleton, errorMessage } from "../../components/States";
import { useLibrarySnapshot } from "../../library/LibraryProvider";
import { usePreferences } from "../../preferences/PreferencesProvider";
import type { CatalogProviderId } from "../../preferences/preferencesStore";
import { watchUrl } from "../player/watchUrl";
import styles from "./HomePage.module.css";

const PROVIDERS: readonly { id: CatalogProviderId; label: string }[] = [
  { id: "animeav1", label: "AnimeAV1" },
  { id: "animeflv", label: "AnimeFLV" },
];

const TYPE_FILTERS = ["Todos", "TV", "Película", "OVA"] as const;
type TypeFilter = (typeof TYPE_FILTERS)[number];

function matchesTypeFilter(animeType: string, filter: TypeFilter): boolean {
  if (filter === "Todos") return true;
  const norm = animeType.toLowerCase();
  if (filter === "TV") return norm.includes("tv") || norm.includes("anime") || norm === "";
  if (filter === "Película") return norm.includes("pel") || norm.includes("movie") || norm.includes("film");
  if (filter === "OVA") return norm.includes("ova") || norm.includes("ona") || norm.includes("especial") || norm.includes("special");
  return true;
}

export function HomePage() {
  const { prefs, store: prefsStore } = usePreferences();
  const [provider, setProvider] = useState<CatalogProviderId>(prefs.defaultProvider);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("Todos");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    setProvider(prefs.defaultProvider);
  }, [prefs.defaultProvider]);

  const catalog = useQuery({
    queryKey: ["catalog", provider, page],
    queryFn: () => animeApi.catalog(page, provider),
  });

  // Precarga la siguiente pagina en segundo plano cuando la actual termina de cargar.
  useEffect(() => {
    if (catalog.data && catalog.data.length > 0) {
      void queryClient.prefetchQuery({
        queryKey: ["catalog", provider, page + 1],
        queryFn: () => animeApi.catalog(page + 1, provider),
        staleTime: 5 * 60_000,
      });
    }
  }, [catalog.data, provider, page, queryClient]);

  const library = useLibrarySnapshot();
  const continueWatching = library.recentProgress(10).filter((p) => p.durationMs > 0 && p.positionMs / p.durationMs < 0.95);

  const items = catalog.data ?? [];
  const filteredItems = items.filter((a) => matchesTypeFilter(a.type, typeFilter));
  const spotlight = page === 1 && items.length > 0 ? items[0] : undefined;

  const openRandomAnime = () => {
    if (items.length === 0) return;
    const pick = items[Math.floor(Math.random() * items.length)]!;
    navigate(`/anime?url=${encodeURIComponent(pick.url)}`);
  };

  const selectProvider = (next: CatalogProviderId) => {
    setProvider(next);
    setPage(1);
    prefsStore.update({ defaultProvider: next });
  };

  return (
    <div className="page">
      <h1 className="visually-hidden">WebinAnime PRO — Inicio</h1>

      {spotlight && (
        <section className={styles.heroBanner} aria-label="Destacado del catálogo">
          <div className={styles.heroMedia}>
            {spotlight.imageUrl && (
              <img
                src={animeApi.imageUrl(spotlight.imageUrl)}
                alt=""
                width={260}
                height={390}
                decoding="async"
                className={styles.heroPoster}
                {...({ fetchpriority: "high" } as Record<string, string>)}
              />
            )}
          </div>
          <div className={styles.heroContent}>
            <div className={styles.heroBadges}>
              <span className={styles.pillPrimary}>Destacado</span>
              {spotlight.type && <span className={styles.pill}>{spotlight.type}</span>}
              {spotlight.year && <span className={styles.pill}>{spotlight.year}</span>}
              <span className={styles.pill}>{spotlight.providerLabel}</span>
            </div>
            <h2 className={styles.heroTitle}>{spotlight.title}</h2>
            <p className={styles.heroSubtitle}>
              Explora los últimos episodios disponibles, guarda tu progreso en local al instante y personaliza tu experiencia de reproducción.
            </p>
            <div className={styles.heroActions}>
              <Link to={`/anime?url=${encodeURIComponent(spotlight.url)}`} className="btn">
                Ver ficha
              </Link>
              <button type="button" className="btn btn--ghost" onClick={openRandomAnime}>
                Sorpréndeme
              </button>
              <Link to="/search" className="btn btn--ghost">
                Explorar géneros
              </Link>
            </div>
          </div>
        </section>
      )}

      {continueWatching.length > 0 && (
        <section>
          <div className={styles.sectionHeader}>
            <h2 className="section-title">Continuar viendo</h2>
            <Link to="/library" className={styles.sectionLink}>
              Ver historial →
            </Link>
          </div>
          <div className="grid">
            {continueWatching.map((p, idx) => (
              <div key={p.episodeUrl} className={styles.continueItem}>
                <AnimeCard
                  url={p.animeUrl}
                  title={p.animeTitle}
                  subtitle={p.episodeTitle}
                  imageUrl={p.imageUrl}
                  progress={p.positionMs / p.durationMs}
                  badge={`${Math.round((p.positionMs / p.durationMs) * 100)}%`}
                  to={watchUrl(p.animeUrl, p.episodeUrl)}
                  priority={idx === 0 && !spotlight}
                  index={idx}
                />
                <div className={styles.continueActions}>
                  <Link to={`/anime?url=${encodeURIComponent(p.animeUrl)}`} className={styles.miniAction}>
                    Ficha
                  </Link>
                  <button
                    type="button"
                    className={styles.miniAction}
                    onClick={() => library.deleteProgress(p.episodeUrl)}
                    aria-label={`Quitar ${p.animeTitle} de continuar viendo`}
                  >
                    Quitar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className={styles.catalogHeader}>
          <h2 className="section-title">Últimos episodios</h2>
          <div className={styles.controlsRow}>
            <div className={styles.pillGroup} role="group" aria-label="Proveedor de catálogo">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={provider === p.id ? `${styles.filterChip} ${styles.filterChipActive}` : styles.filterChip}
                  aria-pressed={provider === p.id}
                  onClick={() => selectProvider(p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className={styles.pillGroup} role="group" aria-label="Filtrar por formato">
              {TYPE_FILTERS.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={typeFilter === filter ? `${styles.filterChip} ${styles.filterChipActive}` : styles.filterChip}
                  aria-pressed={typeFilter === filter}
                  onClick={() => setTypeFilter(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
        </div>

        {catalog.isPending ? (
          <PosterGridSkeleton count={12} />
        ) : catalog.isError ? (
          <ErrorState message={errorMessage(catalog.error)} onRetry={() => void catalog.refetch()} />
        ) : (
          <>
            <div className="grid">
              {filteredItems.map((anime, idx) => {
                const personalRating = prefs.animeMeta[anime.url]?.rating;
                return (
                  <AnimeCard
                    key={anime.url}
                    url={anime.url}
                    title={anime.title}
                    imageUrl={anime.imageUrl}
                    subtitle={[anime.type, anime.year].filter(Boolean).join(" · ")}
                    badge={anime.type || undefined}
                    score={personalRating ?? null}
                    priority={idx < 2 && !spotlight}
                    index={idx}
                  />
                );
              })}
            </div>
            <nav className={styles.pagination} aria-label="Paginación del catálogo">
              <button type="button" className="btn btn--ghost btn--sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                ← Anterior
              </button>
              <span className="muted">Página {page}</span>
              <button type="button" className="btn btn--ghost btn--sm" disabled={items.length === 0} onClick={() => setPage((p) => p + 1)}>
                Siguiente →
              </button>
            </nav>
          </>
        )}
      </section>
    </div>
  );
}
